import { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, User, Ambulance, MapPin, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Reveal } from '@/components/ui/Reveal';
import { useSEO } from '@/lib/seo';
import api from '@/lib/api';
import { parseApiError } from '@/lib/parseApiError';

const INITIAL = {
  nom: '', prenom: '', telephone: '', email: '',
  type_transport: '',
  patient_nom: '', patient_prenom: '', mobilite: '',
  adresse_depart: '', adresse_arrivee: '', date_trajet: '', heure_trajet: '',
  aller_retour: false,
  oxygene: false, accompagnant: false, remarques: '',
  rgpd: false,
};

export default function Devis() {
  useSEO({
    title: "Demande de devis | Groupe Ambulances Beaulon",
    description: "Demandez un devis en ligne pour votre transport sanitaire dans l'Allier : ambulance, VSL, taxi conventionné, transport bariatrique ou lit hospitalier.",
    keywords: "devis ambulance allier, devis transport sanitaire, devis vsl, devis taxi conventionné"
  });

  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rgpd) {
      toast.error('Vous devez accepter la politique RGPD.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/devis', form);
      setSuccess(res.data);
      toast.success('Demande de devis envoyée');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(parseApiError(err, "Erreur lors de l'envoi. Merci de réessayer ou de nous appeler au 04 70 42 72 63."), { duration: 6000 });
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="section-pad">
        <div className="container-app max-w-2xl">
          <div className="card-base p-8 text-center" data-testid="devis-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="h2 mt-4 text-anthracite-950">Demande envoyée</h1>
            <p className="lead mt-2">{success.message}</p>
            <p className="mt-4 text-sm text-anthracite-700">Référence : <span className="font-mono font-semibold">{success.reference}</span></p>
            <Button className="mt-6" onClick={() => { setSuccess(null); setForm(INITIAL); }}>Faire une autre demande</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="hero-wash">
        <div className="container-app pt-14 pb-10">
          <Reveal>
            <p className="badge-soft mb-3">Demande de devis</p>
            <h1 className="h1 text-anthracite-950">Demandez votre devis</h1>
            <p className="lead mt-3 max-w-3xl">Complétez ce formulaire en quelques minutes. Notre équipe vous recontactera rapidement avec une proposition adaptée.</p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-app max-w-4xl">
          <form onSubmit={submit} className="space-y-6" data-testid="quote-form">
            {/* Section 1 : Demandeur */}
            <FormSection icon={User} title="Vos coordonnées" subtitle="Pour vous recontacter">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom *" required value={form.nom} onChange={v => update('nom', v)} testid="devis-nom" />
                <Field label="Prénom *" required value={form.prenom} onChange={v => update('prenom', v)} testid="devis-prenom" />
                <Field label="Téléphone *" required type="tel" value={form.telephone} onChange={v => update('telephone', v)} testid="devis-telephone" />
                <Field label="Email *" required type="email" value={form.email} onChange={v => update('email', v)} testid="devis-email" />
              </div>
            </FormSection>

            {/* Section 2 : Transport */}
            <FormSection icon={Ambulance} title="Type de transport" subtitle="Sélectionnez le mode de transport souhaité">
              <Label className="text-sm font-medium">Type de transport *</Label>
              <Select value={form.type_transport} onValueChange={v => update('type_transport', v)}>
                <SelectTrigger className="mt-2" data-testid="devis-type-transport">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ambulance">Ambulance</SelectItem>
                  <SelectItem value="vsl">VSL</SelectItem>
                  <SelectItem value="taxi_conventionne">Taxi conventionné</SelectItem>
                  <SelectItem value="bariatrique">Transport bariatrique</SelectItem>
                  <SelectItem value="lit_hospitalier">Transport lit hospitalier</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </FormSection>

            {/* Section 3 : Patient */}
            <FormSection icon={User} title="Informations patient" subtitle="Si différent du demandeur">
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <Field label="Nom du patient" value={form.patient_nom} onChange={v => update('patient_nom', v)} />
                <Field label="Prénom du patient" value={form.patient_prenom} onChange={v => update('patient_prenom', v)} />
              </div>
              <Label className="text-sm font-medium">Mobilité</Label>
              <RadioGroup value={form.mobilite} onValueChange={v => update('mobilite', v)} className="mt-2 grid sm:grid-cols-2 gap-2">
                {[
                  ['fauteuil', 'Fauteuil roulant'],
                  ['brancard', 'Brancard'],
                  ['lit', 'Lit hospitalier'],
                  ['bariatrique', 'Patient bariatrique'],
                  ['autonome', 'Autonome'],
                  ['autre', 'Autre'],
                ].map(([k, l]) => (
                  <label key={k} className="flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2 hover:bg-anthracite-100/50">
                    <RadioGroupItem value={k} id={`mob-${k}`} />
                    <span className="text-sm">{l}</span>
                  </label>
                ))}
              </RadioGroup>
            </FormSection>

            {/* Section 4 : Trajet */}
            <FormSection icon={MapPin} title="Détails du trajet" subtitle="Adresses et horaires">
              <div className="space-y-4">
                <Field label="Adresse de départ *" required value={form.adresse_depart} onChange={v => update('adresse_depart', v)} testid="devis-adresse-depart" />
                <Field label="Adresse d'arrivée *" required value={form.adresse_arrivee} onChange={v => update('adresse_arrivee', v)} testid="devis-adresse-arrivee" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Date *" required type="date" value={form.date_trajet} onChange={v => update('date_trajet', v)} testid="devis-date" />
                  <Field label="Heure *" required type="time" value={form.heure_trajet} onChange={v => update('heure_trajet', v)} testid="devis-heure" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.aller_retour} onCheckedChange={(c) => update('aller_retour', !!c)} id="aller-retour" data-testid="devis-aller-retour" />
                  <span className="text-sm">Trajet aller-retour</span>
                </label>
              </div>
            </FormSection>

            {/* Section 5 : Options */}
            <FormSection icon={Info} title="Informations complémentaires" subtitle="Pour mieux préparer votre trajet">
              <div className="grid sm:grid-cols-2 gap-3 mb-4">
                <label className="flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2.5">
                  <Checkbox checked={form.oxygene} onCheckedChange={(c) => update('oxygene', !!c)} />
                  <span className="text-sm">Besoin d'oxygène</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer rounded-lg border px-3 py-2.5">
                  <Checkbox checked={form.accompagnant} onCheckedChange={(c) => update('accompagnant', !!c)} />
                  <span className="text-sm">Avec accompagnant</span>
                </label>
              </div>
              <Label className="text-sm font-medium">Remarques</Label>
              <Textarea value={form.remarques} onChange={e => update('remarques', e.target.value)} placeholder="Précisions, contraintes, numéros de téléphone alternatifs..." className="mt-2 min-h-[100px]" />
            </FormSection>

            {/* RGPD */}
            <div className="rounded-xl border bg-anthracite-100/50 p-4 flex items-start gap-3">
              <Checkbox checked={form.rgpd} onCheckedChange={(c) => update('rgpd', !!c)} id="rgpd" data-testid="devis-rgpd" />
              <label htmlFor="rgpd" className="text-sm text-anthracite-800 leading-relaxed cursor-pointer">
                J'accepte que mes données soient utilisées pour traiter ma demande de devis, conformément à la politique RGPD du Groupe Ambulances Beaulon. *
              </label>
            </div>

            <div className="flex gap-3 justify-end">
              <Button type="submit" disabled={loading} size="lg" data-testid="quote-form-submit-button" className="min-w-[200px]">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi...</> : 'Envoyer ma demande'}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function FormSection({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="card-base p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-anthracite-950">{title}</h3>
          {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, type = 'text', value, onChange, required, testid }) {
  return (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <Input type={type} value={value || ''} onChange={e => onChange(e.target.value)} required={required} className="mt-1.5" data-testid={testid} />
    </div>
  );
}
